import { change } from '@/lib/money'
import { cn } from '@/lib/utils'

export interface ChangeProps {
  now: number | null | undefined
  base: number | null | undefined
  /** `signed` (revenue, profit): up is good, drawn in the delta colours.
   *  `muted` (spend): the arrow says the direction, not whether it is good. */
  tone?: 'signed' | 'muted'
  /** What it is compared with, said first: "그제 대비". */
  label?: string
  className?: string
}

/** A percent change between two finished periods: "▲ 12%", "▼ 8%", "·" when
 *  it rounds to nothing, "—" when there is no base (0 or missing). Never use
 *  it on today, which AdMob is still revising — say "집계 중" instead. */
export function Change({ now, base, tone = 'signed', label, className }: ChangeProps) {
  const c = change(now, base)
  const prefix = label ? <span className="mr-1 text-muted-foreground">{label}</span> : null
  if (c === null) {
    return (
      <span className={cn('whitespace-nowrap tabular-nums text-muted-foreground', className)} title="비교할 값이 없다">
        {prefix}—
      </span>
    )
  }
  const pct = Math.round(Math.abs(c) * 100)
  if (pct === 0) {
    return (
      <span className={cn('whitespace-nowrap tabular-nums text-muted-foreground', className)} aria-label={`${label ? `${label} ` : ''}변화 없음`}>
        {prefix}·
      </span>
    )
  }
  const up = c > 0
  const colour = tone === 'muted' ? 'text-muted-foreground' : up ? 'text-delta-up' : 'text-delta-down'
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)} aria-label={`${label ? `${label} ` : ''}${pct}% ${up ? '증가' : '감소'}`}>
      {prefix}
      <span className={colour}>
        {up ? '▲' : '▼'} {pct}%
      </span>
    </span>
  )
}
